import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import { useMessages } from '../hooks/useMessages';
import { useChatData } from '../context/ChatContext';
import MessageBubble from './MessageBubble';
import { formatDateSeparator, isSameDay } from '../utils/chatHelpers';
import type { ConversationData } from '../context/chatTypes';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';

interface MessageThreadProps {
  conversation: ConversationData;
}

const MessageThread: React.FC<MessageThreadProps> = ({ conversation }) => {
  const { t } = useTranslation();
  const { messages, isLoading, hasMore, loadMore } = useMessages(conversation.id);
  const { lastReadAt } = useChatData();
  const scrollRef = useRef<HTMLDivElement>(null);
  const bottomAnchorRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const isAtBottomRef = useRef(true);
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const prevMessageCountRef = useRef(0);
  const scrollSnapshotRef = useRef(0);

  // Find the index where new (unread) received messages start
  const newMessageIndex = useMemo(() => {
    if (lastReadAt === 0) return -1;

    // If the user sent any message after lastReadAt, they were active in this
    // conversation and have already seen everything — no divider needed
    if (messages.some(m => m.isSentByMe && m.sentAt.getTime() > lastReadAt)) {
      return -1;
    }

    for (let i = 0; i < messages.length; i++) {
      if (!messages[i].isSentByMe && messages[i].sentAt.getTime() > lastReadAt) {
        return i;
      }
    }
    return -1;
  }, [messages, lastReadAt]);

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      bottomAnchorRef.current?.scrollIntoView({ block: 'end' });
      isAtBottomRef.current = true;
      setShowScrollBtn(false);
    });
  }, []);

  // Auto-scroll to bottom on new messages (if user is at/near bottom)
  useEffect(() => {
    const isNewMessage = messages.length > prevMessageCountRef.current;
    const isFirstLoad = prevMessageCountRef.current === 0 && messages.length > 0;

    if (isFirstLoad || (isNewMessage && isAtBottomRef.current)) {
      scrollToBottom();
    }
    prevMessageCountRef.current = messages.length;
  }, [messages.length, scrollToBottom]);

  // Scroll to bottom on conversation change
  useEffect(() => {
    prevMessageCountRef.current = 0;
    isAtBottomRef.current = true;
    scrollToBottom();
  }, [conversation.id, scrollToBottom]);

  const handleScroll = useCallback(() => {
    if (!scrollRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    const atBottom = scrollHeight - scrollTop - clientHeight < 50;
    isAtBottomRef.current = atBottom;
    setShowScrollBtn(!atBottom);
  }, []);

  // Preserve scroll position when older messages are prepended
  useLayoutEffect(() => {
    if (!scrollRef.current || scrollSnapshotRef.current === 0) return;
    const diff = scrollRef.current.scrollHeight - scrollSnapshotRef.current;
    if (diff > 0) {
      scrollRef.current.scrollTop += diff;
    }
    scrollSnapshotRef.current = 0;
  }, [messages.length]);

  // Infinite scroll upward with IntersectionObserver
  useEffect(() => {
    if (!sentinelRef.current || !hasMore) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isLoading && hasMore) {
          // Capture scroll height before loading older messages
          if (scrollRef.current) {
            scrollSnapshotRef.current = scrollRef.current.scrollHeight;
          }
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasMore, isLoading, loadMore]);

  return (
    <div className="chat-message-thread-wrapper">
      <div className="chat-message-thread" ref={scrollRef} onScroll={handleScroll}>
        {/* Sentinel for infinite scroll */}
        {hasMore && (
          <div ref={sentinelRef} className="chat-message-thread__sentinel">
            {isLoading && (
              <div className="chat-message-thread__loading">
                <FontAwesomeIcon icon={faSpinner} spin /> Loading...
              </div>
            )}
          </div>
        )}

        {/* Messages */}
        {messages.length === 0 && !isLoading ? (
          <div className="chat-message-thread__empty">
            <p>No messages yet. Say hello!</p>
          </div>
        ) : (
          messages.map((msg, index) => {
            const prevMsg = index > 0 ? messages[index - 1] : null;
            const showDateSeparator =
              !prevMsg || !isSameDay(msg.sentAt, prevMsg.sentAt);

            const showNewDivider = index === newMessageIndex;
            const isUnread = !msg.isSentByMe && newMessageIndex >= 0 && index >= newMessageIndex;

            return (
              <React.Fragment key={msg.id}>
                {showDateSeparator && (
                  <div className="chat-date-separator">
                    <span>{formatDateSeparator(msg.sentAt)}</span>
                  </div>
                )}
                {showNewDivider && (
                  <div className="chat-new-messages-divider">
                    <span>{t('chat.newMessages', 'New Messages')}</span>
                  </div>
                )}
                <MessageBubble
                  message={msg}
                  showSender={conversation.isGroup}
                  isUnread={isUnread}
                />
              </React.Fragment>
            );
          })
        )}

        {/* Initial loading */}
        {isLoading && messages.length === 0 && (
          <div className="chat-message-thread__loading chat-message-thread__loading--center">
            <FontAwesomeIcon icon={faSpinner} spin /> Loading messages...
          </div>
        )}

        {/* Scroll anchor — scrollIntoView target */}
        <div ref={bottomAnchorRef} data-scroll-anchor />
      </div>

      {/* Scroll to bottom button - outside scroll container */}
      <button
        className={`chat-scroll-bottom-btn ${showScrollBtn ? 'chat-scroll-bottom-btn--visible' : ''}`}
        onClick={scrollToBottom}
      >
        <FontAwesomeIcon icon={faChevronDown} />
      </button>
    </div>
  );
};

export default MessageThread;
